import React, { useMemo, useState } from 'react';

import { Flame, Lightbulb } from 'lucide-react';

import { IMSARMBTSReport } from '../../../../types/services.interfaces/mbts-ux';

import { ConsentLock } from '../ConsentLock';

import { SECTION_IDS, getRubricLevel } from './utils';

interface CoachingHabitsProps {
    report: IMSARMBTSReport;
    isConsentLocked: boolean;
    openConsentDataGather: () => void;
};

export const CoachingHabits = (props: CoachingHabitsProps) => {
    const { report, isConsentLocked, openConsentDataGather } = props;

    const habits = useMemo(() => {
        return report.rubric_parameters.map(parameter => ({
            id: parameter.id,
            skill: parameter.testing_skill.title,
            level: getRubricLevel(parameter.score).level,
            title: `Practice: ${parameter.testing_skill.title}`,
            body: parameter.recommendation,
        }));
    }, [report.rubric_parameters]);

    const [done, setDone] = useState<Record<number, boolean>>({});

    const completedCount = habits.filter(h => done[h.id]).length;

    return (
        <section id={SECTION_IDS.habits} className="mbts_analysis_v2__section mbts_analysis_v2__habits">
            <ConsentLock isConsentLocked={isConsentLocked} openConsentDataGather={openConsentDataGather} />

            <div className="mbts_analysis_v2__section__head">
                <div>
                    <div className="mbts_analysis_v2__section__title_row">
                        <span className="mbts_analysis_v2__section__dot mbts_analysis_v2__section__dot--success" />
                        <h3 className="mbts_analysis_v2__section__title">Actionable Coaching Micro-Habits</h3>
                    </div>
                    <p className="mbts_analysis_v2__section__subtitle">
                        Practice these micro-adjustments in low-stakes situations before your next evaluation.
                    </p>
                </div>
                <div className="mbts_analysis_v2__habits__streak">
                    <span>Streak:</span>
                    <span className="mbts_analysis_v2__habits__streak__pill">
                        <Flame />
                        {completedCount} / {habits.length} Done
                    </span>
                </div>
            </div>

            <div className="mbts_analysis_v2__habits__grid">
                {habits.map(habit => (
                    <label
                        key={habit.id}
                        className={`mbts_analysis_v2__habits__card${done[habit.id] ? ' mbts_analysis_v2__habits__card--done' : ''}`}
                    >
                        <div className="mbts_analysis_v2__habits__card__top">
                            <span className={`mbts_analysis_v2__habits__card__skill mbts_analysis_v2__habits__card__skill--${habit.level}`}>
                                {habit.skill}
                            </span>
                            <input
                                type="checkbox"
                                checked={!!done[habit.id]}
                                onChange={() => setDone(prev => ({ ...prev, [habit.id]: !prev[habit.id] }))}
                            />
                        </div>
                        <h4>{habit.title}</h4>
                        <p>{habit.body}</p>
                        <div className="mbts_analysis_v2__habits__card__tip">
                            <Lightbulb />
                            Practice in your next 1:1 meeting
                        </div>
                    </label>
                ))}
            </div>
        </section>
    );
};
